import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, LogOut, User, Calendar, Star, MessageSquare, Upload, Camera, Crown, Image, X, Plus, Download, ExternalLink, Settings, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CredentialsManager from '../components/CredentialsManager';
import SubscriptionUpgrade from '../components/SubscriptionUpgrade';
import ProfileAnalytics from '../components/ProfileAnalytics';
import NotificationSettings from '../components/NotificationSettings';
import ReferralCard from '../components/ReferralCard';
import OnCallToggle from '../components/OnCallToggle';
import OnCallUrgentRequests from '../components/UrgentRequests';
import MyAvailabilityPanel from '../components/MyAvailabilityPanel';
import ContactModeCard from '../components/ContactModeCard';
import PhoneAccessRequestsInbox from '../components/PhoneAccessRequestsInbox';
import GetAppBanner from '../components/GetAppBanner';
import ConfirmedJobsSection from '../components/ConfirmedJobsSection';
import PartnerPlacement from '../components/PartnerPlacement';
import ExtendedProfileFields from '../components/ExtendedProfileFields';
import { MissedJobsAlert, UrgentJobPaywall } from '../components/UrgentJobAlerts';
import OnCallProUpgrade from '../components/OnCallProUpgrade';
import { MyResponseStats } from '../components/ResponseLeaderboard';
import { useSiteContent } from '../hooks/useSiteContent';
import { detectContactInfo, CONTACT_VALIDATION_MESSAGE } from '../utils/contactDetection';

import { BACKEND_URL } from '../config/api';
import { toLocalYMD } from '../utils/dateHelpers';
import DashboardBanner from '../components/DashboardBanner';
import SearchabilityNudge from '../components/SearchabilityNudge';
import { useOnboardingGate } from '../hooks/useOnboardingGate';

export default function TradesPersonDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useOnboardingGate();
  const { get } = useSiteContent();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [showCredentialForm, setShowCredentialForm] = useState(false);
  const [showSubscriptionModal] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [trades, setTrades] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const [showDateHelper, setShowDateHelper] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [monetizationEnabled, setMonetizationEnabled] = useState(false);
  const [recurringAvailability, setRecurringAvailability] = useState({ enabled: false, days: [] });
  const [portfolioCaption, setPortfolioCaption] = useState('');
  // On Call Pro upgrade modal state
  const [showProUpgrade, setShowProUpgrade] = useState(false);
  const [showUrgentJobPaywall, setShowUrgentJobPaywall] = useState(false);
  const [selectedUrgentJob, setSelectedUrgentJob] = useState(null);
  // Availability mode state
  const [availabilityMode, setAvailabilityMode] = useState('available_from');
  const [availableFromDate, setAvailableFromDate] = useState(null);
  const [availabilityNotes, setAvailabilityNotes] = useState('');
  // Busy mode work preferences
  const [openToWeekendWork, setOpenToWeekendWork] = useState(false);
  const [openToEveningWork, setOpenToEveningWork] = useState(false);
  const [receiveUrgentAlerts, setReceiveUrgentAlerts] = useState(true);
  const [previousAvailabilityMode, setPreviousAvailabilityMode] = useState(null);
  // Profile status state
  const [profileStatus, setProfileStatus] = useState('draft'); // 'draft' | 'published'
  const [savingDraft, setSavingDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [bioError, setBioError] = useState(''); // Contact info validation error
  const [credentialData, setCredentialData] = useState({
    credential_type: '',
    credential_number: '',
    expiry_date: '',
    issuing_body: ''
  });
  const [formData, setFormData] = useState({
    trade: '',
    qualifications: '',
    work_areas: '',
    transport: '',
    daily_rate: '',
    bio: ''
  });

  const fetchPublicSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings/public`);
      setMonetizationEnabled(response.data.tradesperson_monetization_enabled);
    } catch (error) {
      setMonetizationEnabled(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/messages/unread-count`, {
        withCredentials: true
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.warn('TradesPersonDashboard: failed to fetch unread count', error.message);
    }
  }, []);

  const fetchProfile = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tradespeople/profile/${userId}`);
      setProfile(response.data);
      setFormData({
        trade: response.data.trade || '',
        qualifications: response.data.qualifications?.join(', ') || '',
        work_areas: Array.isArray(response.data.work_areas)
          ? response.data.work_areas.join(', ')
          : String(response.data.work_areas || ''),
        transport: response.data.transport || '',
        daily_rate: response.data.daily_rate || '',
        bio: response.data.bio || ''
      });
      if (response.data.available_dates) {
        setSelectedDates(response.data.available_dates.map(d => new Date(d)));
      }
      if (response.data.recurring_availability) {
        setRecurringAvailability(response.data.recurring_availability);
      }
      if (response.data.availability_mode) {
        setAvailabilityMode(response.data.availability_mode);
      }
      if (response.data.available_from_date) {
        setAvailableFromDate(new Date(response.data.available_from_date));
      }
      if (response.data.availability_notes) {
        setAvailabilityNotes(response.data.availability_notes);
      }
      if (response.data.open_to_weekend_work !== undefined) {
        setOpenToWeekendWork(response.data.open_to_weekend_work || false);
      }
      if (response.data.open_to_evening_work !== undefined) {
        setOpenToEveningWork(response.data.open_to_evening_work || false);
      }
      if (response.data.receive_urgent_alerts_while_busy !== undefined) {
        setReceiveUrgentAlerts(response.data.receive_urgent_alerts_while_busy !== false);
      }
      if (response.data.previous_availability_mode) {
        setPreviousAvailabilityMode(response.data.previous_availability_mode);
      }
      if (response.data.profile_status) {
        setProfileStatus(response.data.profile_status);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setShowProfileForm(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTrades = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trades`);
      setTrades(response.data);
    } catch (error) {
      console.error('Failed to fetch trades');
    }
  }, []);

  const fetchCredentials = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tradespeople/credentials`, {
        withCredentials: true
      });
      setCredentials(response.data);
    } catch (error) {
      console.error('Failed to fetch credentials');
    }
  }, []);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tradespeople/subscription`, {
        withCredentials: true
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription');
    }
  }, []);

  useEffect(() => {
    // Initial setup - runs once on mount
    const initAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true
        });
        
        if (response.data.user_type !== 'tradesperson') {
          navigate('/login');
          return;
        }
        
        setUser(response.data);
        fetchProfile(response.data.user_id);
        fetchCredentials();
        fetchSubscription();
        fetchUnreadCount();
      } catch (error) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    
    initAuth();
    fetchTrades();
    fetchPublicSettings();
    
    // Poll for unread messages every 30 seconds
    const pollInterval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(pollInterval);
  }, [navigate, fetchProfile, fetchCredentials, fetchSubscription, fetchUnreadCount, fetchTrades, fetchPublicSettings]);

  // Handle setup=availability redirect - scroll to availability section
  useEffect(() => {
    if (searchParams.get('setup') === 'availability' && profile && !loading) {
      setTimeout(() => {
        const availabilitySection = document.getElementById('availability-section');
        if (availabilitySection) {
          availabilitySection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight effect
          availabilitySection.classList.add('ring-2', 'ring-orange-500', 'ring-offset-2');
          setTimeout(() => {
            availabilitySection.classList.remove('ring-2', 'ring-orange-500', 'ring-offset-2');
          }, 3000);
        }
      }, 500);
    }
  }, [searchParams, profile, loading]);

  // Compute if profile is complete (can be published)
  const isProfileComplete = () => {
    // Only availability is mandatory to publish a profile
    // All other fields are optional but recommended
    return isAvailabilitySet();
  };
  
  // Get list of missing requirements for the warning display
  // Only availability is mandatory - other fields are recommendations
  const getMissingRequirements = () => {
    const missing = [];
    if (!isAvailabilitySet()) missing.push('Set your availability status');
    return missing;
  };
  
  // Get recommendations for improving profile (not blocking publish)
  const getProfileRecommendations = () => {
    const recommendations = [];
    if (!formData.trade) recommendations.push('Select a trade to help employers find you');
    if (!formData.work_areas || formData.work_areas.trim().length === 0) recommendations.push('Add work areas to appear in local searches');
    if (!formData.daily_rate || parseFloat(formData.daily_rate) <= 0) recommendations.push('Set a daily rate so employers know your pricing');
    if (!formData.bio || formData.bio.trim().length < 20) recommendations.push('Add a bio to stand out from other tradespeople');
    return recommendations;
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'tradesperson') {
        navigate('/login');
        return;
      }
      
      // Check if user is waitlisted
      if (response.data.region_status === 'waitlisted') {
        navigate('/waitlist-status');
        return;
      }
      
      setUser(response.data);
      fetchProfile(response.data.user_id);
      fetchCredentials();
      fetchSubscription();
      fetchUnreadCount();
    } catch (error) {
      navigate('/login');
    }
  };

  const handleRecurringChange = async (day) => {
    const newDays = recurringAvailability.days.includes(day)
      ? recurringAvailability.days.filter(d => d !== day)
      : [...recurringAvailability.days, day];
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/tradespeople/recurring-availability`,
        { days: newDays, enabled: newDays.length > 0 },
        { withCredentials: true }
      );
      setRecurringAvailability({ enabled: newDays.length > 0, days: newDays });
      toast.success('Recurring availability updated');
      
      // Refresh profile to get updated dates
      if (user) {
        fetchProfile(user.user_id);
      }
    } catch (error) {
      toast.error('Failed to update recurring availability');
    }
  };

  const handlePortfolioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingPortfolio(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    if (portfolioCaption) {
      formDataUpload.append('caption', portfolioCaption);
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/tradespeople/portfolio/upload`,
        formDataUpload,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success('Photo added to portfolio!');
      setPortfolioCaption('');
      if (user) {
        fetchProfile(user.user_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPortfolio(false);
    }
  };

  const handleDeletePortfolioImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/tradespeople/portfolio/${imageId}`, {
        withCredentials: true
      });
      toast.success('Photo deleted');
      if (user) {
        fetchProfile(user.user_id);
      }
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/tradespeople/profile/upload-image`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success('Profile picture updated!');
      fetchProfile(user.user_id);
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!window.confirm('Remove profile picture?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/tradespeople/profile/remove-image`,
        { withCredentials: true }
      );
      toast.success('Profile picture removed');
      fetchProfile(user.user_id);
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (date) => {
    if (!date) return;
    
    const dateStr = toLocalYMD(date);
    const existing = selectedDates.find(d => toLocalYMD(d) === dateStr);
    
    if (existing) {
      setSelectedDates(selectedDates.filter(d => toLocalYMD(d) !== dateStr));
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };

  const addWeekdaysInMonth = (month, year) => {
    const dates = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay();
      // 1-5 are Monday to Friday
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        dates.push(date);
      }
    }
    
    setSelectedDates([...selectedDates, ...dates]);
    toast.success(`Added ${dates.length} weekdays`);
  };

  const addAllDaysInMonth = (month, year) => {
    const dates = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      dates.push(new Date(year, month, day));
    }
    
    setSelectedDates([...selectedDates, ...dates]);
    toast.success(`Added ${dates.length} days`);
  };

  const clearAllDates = () => {
    setSelectedDates([]);
    toast.success('All dates cleared');
  };

  const saveAvailability = async () => {
    try {
      // Use the dedicated availability-mode endpoint for better handling
      const payload = {
        availability_mode: availabilityMode,
        available_from_date: availableFromDate ? toLocalYMD(availableFromDate) : null,
        available_dates: selectedDates.map(d => toLocalYMD(d)),
        availability_notes: availabilityNotes || null,
        // Busy-specific work preferences
        open_to_weekend_work: availabilityMode === 'busy' ? openToWeekendWork : null,
        open_to_evening_work: availabilityMode === 'busy' ? openToEveningWork : null,
        receive_urgent_alerts: availabilityMode === 'busy' ? receiveUrgentAlerts : null
      };
      
      await axios.put(`${BACKEND_URL}/api/tradespeople/availability-mode`, payload, {
        withCredentials: true
      });
      toast.success('Availability updated!');
      fetchProfile(user.user_id);
    } catch (error) {
      toast.error('Failed to save availability');
    }
  };

  const restorePreviousAvailability = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/tradespeople/availability-mode/restore-previous`, {}, {
        withCredentials: true
      });
      toast.success('Previous availability restored!');
      fetchProfile(user.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore previous availability');
    }
  };

  const isAvailabilitySet = () => {
    // Check current form state, not server state
    if (availabilityMode === 'available_now') return true;
    if (availabilityMode === 'available_from' && availableFromDate) return true;
    if (availabilityMode === 'custom_dates' && selectedDates.length > 0) return true;
    if (availabilityMode === 'busy') return true; // Busy is a valid selection
    return false;
  };

  const handleSubmit = async (e, publish = false) => {
    e.preventDefault();
    
    // Clear previous bio error
    setBioError('');
    
    // Validate bio for contact information
    if (formData.bio) {
      const contactCheck = detectContactInfo(formData.bio);
      if (contactCheck.hasContact) {
        setBioError(CONTACT_VALIDATION_MESSAGE);
        toast.error('Your bio contains contact information that is not allowed');
        return;
      }
    }
    
    // Validate required fields before attempting publish
    if (publish) {
      const missing = getMissingRequirements();
      if (missing.length > 0) {
        toast.error(`Cannot publish. Missing: ${missing.join(', ')}`);
        return;
      }
    }
    
    const payload = {
      trade: formData.trade,
      qualifications: formData.qualifications.split(',').map(q => q.trim()).filter(q => q),
      work_areas: formData.work_areas.split(',').map(a => a.trim()).filter(a => a),
      transport: formData.transport,
      daily_rate: parseFloat(formData.daily_rate) || 0,
      available_dates: selectedDates.map(d => toLocalYMD(d)),
      bio: formData.bio,
      // Availability mode fields
      availability_mode: availabilityMode,
      available_from_date: availableFromDate ? toLocalYMD(availableFromDate) : null,
      availability_notes: availabilityNotes || null,
      // Profile status - only set to published if explicitly publishing
      profile_status: publish ? 'published' : (profile?.profile_status || 'draft')
    };

    if (publish) {
      setPublishing(true);
    } else {
      setSavingDraft(true);
    }

    try {
      const url = profile 
        ? `${BACKEND_URL}/api/tradespeople/profile${publish ? '?publish=true' : ''}`
        : `${BACKEND_URL}/api/tradespeople/profile${publish ? '?publish=true' : ''}`;
      
      let response;
      if (profile) {
        response = await axios.put(url, payload, { withCredentials: true });
        toast.success(publish ? 'Profile published!' : 'Draft saved!');
      } else {
        response = await axios.post(url, payload, { withCredentials: true });
        toast.success(publish ? 'Profile created and published!' : 'Draft created!');
      }
      
      // Update local profile status immediately from response
      if (response.data.profile_status) {
        setProfileStatus(response.data.profile_status);
      }
      
      // Refresh full profile data
      await fetchProfile(user.user_id);
      setShowProfileForm(false);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (errorDetail) {
        toast.error(errorDetail);
      } else {
        toast.error(publish ? 'Failed to publish profile' : 'Failed to save draft');
      }
      console.error('Profile save error:', error.response?.data || error);
    } finally {
      setSavingDraft(false);
      setPublishing(false);
    }
  };

  const handleSaveDraft = (e) => handleSubmit(e, false);
  const handlePublish = (e) => handleSubmit(e, true);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-orange-500" data-testid="dashboard-logo" />
            <h1 className="text-xl font-bold text-slate-900">OnsiteHand</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600" data-testid="user-name">Hi, {user?.name}</span>
            <button
              onClick={() => navigate('/change-password')}
              className="btn-secondary text-sm"
              data-testid="change-password-btn"
            >
              Change Password
            </button>
            <button
              onClick={() => navigate('/messages')}
              className="btn-secondary text-sm flex items-center gap-2 relative"
              data-testid="messages-btn"
            >
              <MessageSquare className="h-4 w-4" />
              Messages
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="unread-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="settings-btn"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </button>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DashboardBanner target="tradesperson" />
        <SearchabilityNudge />
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <img 
              src="/icons/onsitehand-icon.png" 
              alt="OnsiteHand" 
              className="h-16 w-16 rounded-xl shadow-md"
            />
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2 font-heading uppercase tracking-tight" data-testid="dashboard-title">Tradesperson Dashboard</h2>
              <p className="text-slate-600">Manage your profile and availability</p>
            </div>
          </div>
        </div>

        {!profile && !showProfileForm && (
          <div className="card mb-8 text-center py-12" data-testid="create-profile-prompt">
            <User className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">Create Your Profile</h3>
            <p className="text-slate-600 mb-6">Set up your profile to start getting hired</p>
            <button
              onClick={() => setShowProfileForm(true)}
              className="btn-accent"
              data-testid="create-profile-btn"
            >
              Create Profile
            </button>
          </div>
        )}

        {profile && !showProfileForm && (
          <div className="space-y-6">
            {/* Canonical completion nudge lives above (SearchabilityNudge).
                The legacy red "profile is NOT visible" banner was removed
                in Phase 5.2 so we surface a single amber prompt driven
                entirely by backend reason codes. */}

            {/* Profile Card */}
            <div className="card" data-testid="profile-card">
              <div className="flex flex-col md:flex-row gap-6 mb-6">
                {/* Profile Picture */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center">
                      {profile.profile_image ? (
                        <img
                          src={`${BACKEND_URL}${profile.profile_image}`}
                          alt="Profile"
                          className="w-full h-full object-cover"
                          data-testid="profile-image"
                        />
                      ) : (
                        <User className="h-16 w-16 text-slate-400" />
                      )}
                    </div>
                    <label
                      htmlFor="profile-image-upload"
                      className="absolute bottom-0 right-0 bg-orange-500 text-white p-2 rounded-full cursor-pointer hover:bg-orange-600 transition-all"
                      data-testid="upload-image-btn"
                    >
                      <Camera className="h-4 w-4" />
                    </label>
                    <input
                      id="profile-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                  {uploadingImage && (
                    <p className="text-sm text-slate-600 mt-2">Uploading...</p>
                  )}
                  {profile.profile_image && (
                    <button
                      onClick={handleRemoveImage}
                      className="text-xs text-red-600 hover:text-red-700 mt-2"
                      data-testid="remove-image-btn"
                    >
                      Remove Photo
                    </button>
                  )}
                </div>

                {/* Profile Info */}
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold text-slate-900">{profile.trade}</h3>
                        {/* Visibility Status Chip — driven by is_searchable
                            (the canonical marketplace flag) so it can never
                            contradict the SearchabilityNudge above. */}
                        {profile.is_searchable ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full uppercase" data-testid="visibility-chip-live">
                            Live
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full" data-testid="visibility-chip-limited">
                            Not visible yet
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="h-5 w-5 text-orange-500 fill-orange-500" />
                        <span className="font-semibold text-slate-900" data-testid="average-rating">{profile.average_rating || 0}</span>
                        <span className="text-slate-600" data-testid="total-reviews">({profile.total_reviews || 0} reviews)</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowProfileForm(true)}
                      className="btn-secondary"
                      data-testid="edit-profile-btn"
                    >
                      Edit Profile
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Daily Rate</p>
                      <p className="text-lg font-semibold text-slate-900" data-testid="daily-rate">£{profile.daily_rate}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Transport</p>
                      <p className="text-lg font-semibold text-slate-900" data-testid="transport">{profile.transport}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Qualifications</p>
                      <p className="text-slate-900" data-testid="qualifications">{profile.qualifications?.join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Work Areas</p>
                      <p className="text-slate-900" data-testid="work-areas">{Array.isArray(profile.work_areas) ? profile.work_areas.join(', ') : (profile.work_areas || '')}</p>
                    </div>
                  </div>

                  {profile.bio && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <p className="text-sm font-medium text-slate-500 mb-1">Bio</p>
                      <p className="text-slate-900" data-testid="bio">{profile.bio}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Analytics — free marketplace: no subscription gate, no upgrade CTA */}
            <ProfileAnalytics 
              subscription={null}
              onUpgrade={() => {}}
              monetizationEnabled={false}
            />

            {/* Referral Program */}
            <ReferralCard user={user} />

            {/* Partner Placements - Tradesperson Dashboard */}
            <div className="mt-6" data-testid="tradesperson-partner-placements">
              <PartnerPlacement 
                placement="tradesperson-dashboard" 
                userType="tradesperson" 
                region={Array.isArray(profile?.work_areas) ? profile.work_areas[0] : profile?.work_areas}
              />
            </div>

            {/* On Call Toggle */}
            {profile && profile.profile_status === 'published' && (
              <>
                {/* Missed Jobs Alert - shows for free users who missed urgent jobs */}
                <MissedJobsAlert onUpgrade={() => setShowProUpgrade(true)} />
                
                <OnCallToggle onStatusChange={() => fetchProfile(user.user_id)} />

                {/* Install-app banner (dismissible, hidden when already installed) */}
                <GetAppBanner />

                {/* Confirmed jobs (accepted offers) — must always be visible
                    so a tradesperson can re-open the job details after the
                    offer card has moved out of pending. */}
                <ConfirmedJobsSection userType="tradesperson" />

                {/* My Availability — calendar self-management */}
                <MyAvailabilityPanel />
                <ContactModeCard />
                <PhoneAccessRequestsInbox />
                
                {/* Response Time Stats */}
                <MyResponseStats onUpgrade={() => setShowProUpgrade(true)} />
                
                <OnCallUrgentRequests />
              </>
            )}

            {/* Availability Section */}
            <div id="availability-section" className="card" data-testid="availability-card">
              <h3 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-orange-500" />
                Availability
              </h3>
              <p className="text-sm text-slate-500 mb-2">Let employers know when you can start work</p>
              
              {/* Availability explanation tooltip */}
              <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-xs text-slate-600">
                    <p className="font-medium text-slate-700">How availability works:</p>
                    <ul className="mt-1 space-y-0.5 list-disc list-inside">
                      <li><strong>Available Now</strong> – You appear at the top of search results, marked as ready to work</li>
                      <li><strong>Available From</strong> – Shows employers when you'll next be free</li>
                      <li><strong>Specific Dates</strong> – Perfect for part-time or flexible schedules</li>
                      <li><strong>Busy</strong> – Hides you from searches temporarily (your profile stays intact)</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Availability Mode Options */}
              <div className="space-y-4 mb-6">
                {/* Option A: Available Now */}
                <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  availabilityMode === 'available_now' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`} data-testid="availability-mode-now">
                  <input
                    type="radio"
                    name="availability_mode"
                    value="available_now"
                    checked={availabilityMode === 'available_now'}
                    onChange={(e) => setAvailabilityMode(e.target.value)}
                    className="w-4 h-4 text-green-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Available Now</p>
                    <p className="text-sm text-slate-600">Ready to take on work immediately</p>
                  </div>
                  {availabilityMode === 'available_now' && (
                    <span className="px-2 py-1 bg-green-500 text-white text-xs font-medium rounded">Active</span>
                  )}
                </label>

                {/* Option B: Available From Date */}
                <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  availabilityMode === 'available_from' 
                    ? 'border-orange-500 bg-orange-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`} data-testid="availability-mode-from">
                  <input
                    type="radio"
                    name="availability_mode"
                    value="available_from"
                    checked={availabilityMode === 'available_from'}
                    onChange={(e) => setAvailabilityMode(e.target.value)}
                    className="w-4 h-4 text-orange-500 mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Available From</p>
                    <p className="text-sm text-slate-600 mb-2">Finishing a job? Set your next available date</p>
                    {availabilityMode === 'available_from' && (
                      <DatePicker
                        selected={availableFromDate}
                        onChange={(date) => setAvailableFromDate(date)}
                        minDate={new Date()}
                        dateFormat="dd MMM yyyy"
                        placeholderText="Pick your start date"
                        className="w-full max-w-xs"
                        data-testid="available-from-date-picker"
                      />
                    )}
                  </div>
                </label>

                {/* Option C: Custom Dates */}
                <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  availabilityMode === 'custom_dates' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`} data-testid="availability-mode-custom">
                  <input
                    type="radio"
                    name="availability_mode"
                    value="custom_dates"
                    checked={availabilityMode === 'custom_dates'}
                    onChange={(e) => setAvailabilityMode(e.target.value)}
                    className="w-4 h-4 text-blue-500 mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Specific Dates Only</p>
                    <p className="text-sm text-slate-600 mb-2">Pick the exact days you're free to work</p>
                    {availabilityMode === 'custom_dates' && (
                      <div className="mt-3">
                        <DatePicker
                          selected={null}
                          onChange={handleDateChange}
                          minDate={new Date()}
                          highlightDates={selectedDates}
                          inline
                          data-testid="custom-dates-picker"
                        />
                        <div className="mt-3">
                          <div className="flex flex-wrap gap-2 mb-3">
                            <button
                              type="button"
                              onClick={() => {
                                const today = new Date();
                                const dates = [];
                                for (let i = 0; i < 7; i++) {
                                  const date = new Date(today);
                                  date.setDate(today.getDate() + i);
                                  dates.push(date);
                                }
                                setSelectedDates([...selectedDates, ...dates]);
                                toast.success('Added next 7 days');
                              }}
                              className="btn-secondary text-xs"
                              data-testid="add-next-week-btn"
                            >
                              + Next 7 Days
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const today = new Date();
                                addAllDaysInMonth(today.getMonth(), today.getFullYear());
                              }}
                              className="btn-secondary text-xs"
                              data-testid="add-next-month-btn"
                            >
                              + This Month
                            </button>
                            <button
                              type="button"
                              onClick={clearAllDates}
                              className="btn-secondary text-xs text-red-600"
                              data-testid="clear-dates-btn"
                            >
                              Clear All
                            </button>
                          </div>
                          {selectedDates.length > 0 && (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                              {selectedDates.slice(0, 12).map((date, idx) => (
                                <div
                                  key={idx}
                                  className="px-2 py-1 bg-blue-100 text-blue-800 border border-blue-200 rounded text-center text-xs font-medium"
                                >
                                  {date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                </div>
                              ))}
                              {selectedDates.length > 12 && (
                                <div className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-center text-xs font-medium">
                                  +{selectedDates.length - 12} more
                                </div>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-slate-500 mt-2">
                            {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''} selected
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </label>

                {/* Option D: Busy / Temporarily Unavailable */}
                <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  availabilityMode === 'busy' 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`} data-testid="availability-mode-busy">
                  <input
                    type="radio"
                    name="availability_mode"
                    value="busy"
                    checked={availabilityMode === 'busy'}
                    onChange={(e) => setAvailabilityMode(e.target.value)}
                    className="w-4 h-4 text-amber-500 mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-slate-900">{get('tradesperson.status.busy', 'Busy')} / {get('tradesperson.status.temporarily_unavailable', 'Taking a Break')}</p>
                      {availabilityMode === 'busy' && (
                        <span className="px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded">{get('tradesperson.status.busy', 'Busy')}</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Hide from searches temporarily – your profile stays safe</p>
                    
                    {availabilityMode === 'busy' && (
                      <div className="mt-3 space-y-3">
                        {/* Info box */}
                        <div className="p-3 bg-amber-100 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-800 font-medium flex items-center gap-1">
                            <AlertCircle className="h-3.5 w-3.5" />
                            You won't appear in search results while Busy
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            Your reviews, trust score, and profile stay exactly as they are
                          </p>
                        </div>
                        
                        {/* Work preferences */}
                        <div className="p-3 bg-white border border-amber-200 rounded-lg space-y-2">
                          <p className="text-sm font-medium text-slate-800">Still open to certain work?</p>
                          
                          <label className="flex items-center gap-2 cursor-pointer" data-testid="open-to-weekend-work">
                            <input
                              type="checkbox"
                              checked={openToWeekendWork}
                              onChange={(e) => setOpenToWeekendWork(e.target.checked)}
                              className="w-4 h-4 rounded text-amber-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-sm text-slate-700">Open to weekend work</span>
                          </label>
                          
                          <label className="flex items-center gap-2 cursor-pointer" data-testid="open-to-evening-work">
                            <input
                              type="checkbox"
                              checked={openToEveningWork}
                              onChange={(e) => setOpenToEveningWork(e.target.checked)}
                              className="w-4 h-4 rounded text-amber-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-sm text-slate-700">Open to evening work</span>
                          </label>
                          
                          <label className="flex items-center gap-2 cursor-pointer" data-testid="receive-urgent-alerts">
                            <input
                              type="checkbox"
                              checked={receiveUrgentAlerts}
                              onChange={(e) => setReceiveUrgentAlerts(e.target.checked)}
                              className="w-4 h-4 rounded text-amber-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-sm text-slate-700">Still receive urgent job alerts</span>
                          </label>
                        </div>
                        
                        {/* Restore previous availability */}
                        {previousAvailabilityMode && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              restorePreviousAvailability();
                            }}
                            className="text-sm text-amber-700 hover:text-amber-800 underline"
                            data-testid="restore-previous-availability"
                          >
                            Restore previous availability ({previousAvailabilityMode.replace('_', ' ')})
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* Availability Notes */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={availabilityNotes}
                  onChange={(e) => setAvailabilityNotes(e.target.value)}
                  placeholder="e.g., Weekends only, Flexible hours, Available for urgent jobs..."
                  className="w-full h-20 resize-none"
                  data-testid="availability-notes"
                />
              </div>

              {/* Save Button */}
              <button
                onClick={saveAvailability}
                className="btn-accent w-full sm:w-auto"
                data-testid="save-availability-btn"
              >
                Save Availability
              </button>

              {/* Calendar Export Options - only show if dates are set */}
              {availabilityMode === 'custom_dates' && selectedDates.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200 flex gap-2">
                  <button
                    onClick={async () => {
                      try {
                        const response = await axios.get(
                          `${BACKEND_URL}/api/tradespeople/calendar/export`,
                          { withCredentials: true, responseType: 'blob' }
                        );
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'availability.ics');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success('Calendar exported!');
                      } catch (error) {
                        toast.error('Failed to export calendar');
                      }
                    }}
                    className="btn-secondary text-xs flex items-center gap-1"
                    data-testid="export-calendar-btn"
                  >
                    <Download className="h-3 w-3" />
                    Export iCal
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const response = await axios.get(
                          `${BACKEND_URL}/api/tradespeople/calendar/google-url`,
                          { withCredentials: true }
                        );
                        if (response.data.urls && response.data.urls.length > 0) {
                          window.open(response.data.urls[0].url, '_blank');
                        }
                      } catch (error) {
                        toast.error('Failed to get Google Calendar link');
                      }
                    }}
                    className="btn-secondary text-xs flex items-center gap-1"
                    data-testid="export-google-btn"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Add to Google
                  </button>
                </div>
              )}
            </div>

            {/* Portfolio Gallery */}
            <div className="card" data-testid="portfolio-card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Image className="h-5 w-5 text-orange-500" />
                  Work Portfolio
                </h3>
                <label className="btn-secondary cursor-pointer flex items-center gap-2 text-sm">
                  <Plus className="h-4 w-4" />
                  Add Photo
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePortfolioUpload}
                    className="hidden"
                    disabled={uploadingPortfolio}
                    data-testid="portfolio-upload-input"
                  />
                </label>
              </div>
              
              {profile.portfolio && profile.portfolio.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {profile.portfolio.map((img) => (
                    <div key={img.image_id} className="relative group" data-testid={`portfolio-item-${img.image_id}`}>
                      <img
                        src={`${BACKEND_URL}${img.url}`}
                        alt={img.caption || 'Portfolio work'}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => handleDeletePortfolioImage(img.image_id)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete photo"
                        data-testid={`delete-portfolio-${img.image_id}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                      {img.caption && (
                        <p className="text-xs text-slate-600 mt-1 truncate">{img.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <Image className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-600 font-medium">Show off your best work</p>
                  <p className="text-sm text-slate-500 mt-1">Profiles with photos get 3x more enquiries</p>
                </div>
              )}
              
              {uploadingPortfolio && (
                <div className="mt-4 flex items-center gap-2 text-orange-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                  Uploading...
                </div>
              )}
            </div>

            {/* Reviews */}
            {profile.reviews && profile.reviews.length > 0 && (
              <div className="card" data-testid="reviews-section">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Star className="h-5 w-5 text-orange-500" />
                  Recent Reviews
                </h3>
                <div className="space-y-4">
                  {profile.reviews.slice(0, 5).map((review, idx) => (
                    <div key={idx} className="border-b border-slate-200 pb-4 last:border-0" data-testid={`review-${idx}`}>
                      <div className="flex gap-4 mb-2">
                        <div>
                          <p className="text-sm font-medium text-slate-700">Punctuality</p>
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < review.punctuality
                                    ? 'text-orange-500 fill-orange-500'
                                    : 'text-slate-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-700">Professionalism</p>
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < review.professionalism
                                    ? 'text-orange-500 fill-orange-500'
                                    : 'text-slate-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-700">Quality</p>
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < review.quality
                                    ? 'text-orange-500 fill-orange-500'
                                    : 'text-slate-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      {review.comment && (
                        <p className="text-slate-600 text-sm italic">"{review.comment}"</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Credentials Manager */}
            <CredentialsManager />

            {/* Notification Settings */}
            <div className="card" data-testid="notification-settings-card">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5 text-orange-500" />
                Notification Settings
              </h3>
              <NotificationSettings />
            </div>

            {/* FREE MARKETPLACE: Subscription Upgrade Card hidden — kept dormant for future reuse. */}
            {false && (
              <div className="card" data-testid="subscription-card">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                      <Crown className="h-5 w-5 text-orange-500" />
                      Boost Your Profile
                    </h3>
                    <p className="text-slate-600 text-sm mt-1">
                      {subscription?.plan_type && subscription.plan_type !== 'free' 
                        ? `Current plan: ${subscription.plan_type.charAt(0).toUpperCase() + subscription.plan_type.slice(1)}`
                        : 'Upgrade to get more visibility and features'}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/plans')}
                    className="btn-accent flex items-center gap-2"
                    data-testid="upgrade-btn"
                  >
                    <Crown className="h-4 w-4" />
                    {subscription?.plan_type && subscription.plan_type !== 'free' ? 'Manage Plan' : 'Upgrade'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {showProfileForm && (
          <div className="card" data-testid="profile-form">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              {profile ? 'Edit Profile' : 'Create Profile'}
            </h3>
            
            {/* Info box about requirements */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-800">Only availability is required to go live</p>
                  <p className="text-xs text-blue-700 mt-1">All other fields are optional but highly recommended to help employers find and choose you. The more complete your profile, the better your chances of getting hired.</p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSaveDraft} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Trade
                    <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Recommended</span>
                  </label>
                  <select
                    name="trade"
                    value={formData.trade}
                    onChange={handleChange}
                    className="w-full"
                    data-testid="trade-select"
                  >
                    <option value="">What's your main trade?</option>
                    {trades.map((trade) => (
                      <option key={trade.trade_id} value={trade.name}>
                        {trade.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Pick the trade you want to appear under in searches</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Daily Rate (£)
                    <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Recommended</span>
                  </label>
                  <input
                    type="number"
                    name="daily_rate"
                    value={formData.daily_rate}
                    onChange={handleChange}
                    placeholder="Your typical day rate"
                    step="0.01"
                    className="w-full"
                    data-testid="daily-rate-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Employers use this as a guide – you can negotiate per job</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Transport
                    <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-slate-100 text-slate-600 rounded">Optional</span>
                  </label>
                  <select
                    name="transport"
                    value={formData.transport}
                    onChange={handleChange}
                    className="w-full"
                    data-testid="transport-select"
                  >
                    <option value="">How do you travel to sites?</option>
                    <option value="Own vehicle">Own vehicle</option>
                    <option value="Public transport">Public transport</option>
                    <option value="Bicycle">Bicycle</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Qualifications
                    <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-slate-100 text-slate-600 rounded">Optional</span>
                  </label>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    {profile?.qualifications ? (
                      <p className="text-slate-700 text-sm">{profile.qualifications}</p>
                    ) : (
                      <p className="text-slate-500 text-sm italic">No qualifications verified yet</p>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-2 flex items-start gap-2">
                    <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span>Qualifications must be verified by admin. Use the "Credentials" section below to upload certificates for verification.</span>
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Work Areas
                  <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Recommended</span>
                </label>
                <input
                  type="text"
                  name="work_areas"
                  value={formData.work_areas}
                  onChange={handleChange}
                  placeholder="E.g., Central London, South East, Manchester"
                  className="w-full"
                  data-testid="work-areas-input"
                />
                <p className="text-xs text-slate-500 mt-1">Comma-separated cities or areas you'll travel to</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Availability Status
                  <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-green-100 text-green-700 rounded">Required to publish</span>
                </label>
                <div className="space-y-2" data-testid="inline-availability-picker">
                  {/* Available Now */}
                  <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    availabilityMode === 'available_now'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`} data-testid="publish-mode-now">
                    <input
                      type="radio"
                      name="publish_availability_mode"
                      value="available_now"
                      checked={availabilityMode === 'available_now'}
                      onChange={(e) => setAvailabilityMode(e.target.value)}
                      className="w-4 h-4 text-green-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">Available Now</p>
                      <p className="text-xs text-slate-500">Ready to take on work immediately</p>
                    </div>
                    {availabilityMode === 'available_now' && (
                      <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded shrink-0">Active</span>
                    )}
                  </label>

                  {/* Available From */}
                  <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    availabilityMode === 'available_from'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`} data-testid="publish-mode-from">
                    <input
                      type="radio"
                      name="publish_availability_mode"
                      value="available_from"
                      checked={availabilityMode === 'available_from'}
                      onChange={(e) => setAvailabilityMode(e.target.value)}
                      className="w-4 h-4 text-orange-500 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">Available From</p>
                      <p className="text-xs text-slate-500 mb-1">Set when you'll next be free</p>
                      {availabilityMode === 'available_from' && (
                        <DatePicker
                          selected={availableFromDate}
                          onChange={(date) => setAvailableFromDate(date)}
                          minDate={new Date()}
                          dateFormat="dd MMM yyyy"
                          placeholderText="Pick your start date"
                          className="w-full max-w-[200px] text-sm border-slate-300 rounded-md"
                          data-testid="publish-from-date"
                        />
                      )}
                    </div>
                  </label>

                  {/* Specific Dates */}
                  <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    availabilityMode === 'custom_dates'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`} data-testid="publish-mode-custom">
                    <input
                      type="radio"
                      name="publish_availability_mode"
                      value="custom_dates"
                      checked={availabilityMode === 'custom_dates'}
                      onChange={(e) => setAvailabilityMode(e.target.value)}
                      className="w-4 h-4 text-blue-500 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">Specific Dates Only</p>
                      {availabilityMode === 'custom_dates' ? (
                        selectedDates.length > 0 ? (
                          <p className="text-xs text-blue-700 mt-0.5">{selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''} selected — manage dates in the Availability section above</p>
                        ) : (
                          <p className="text-xs text-amber-600 mt-0.5">Use the Availability section above to select your dates</p>
                        )
                      ) : (
                        <p className="text-xs text-slate-500">Pick exact days you're free to work</p>
                      )}
                    </div>
                  </label>

                  {/* Busy */}
                  <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    availabilityMode === 'busy'
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`} data-testid="publish-mode-busy">
                    <input
                      type="radio"
                      name="publish_availability_mode"
                      value="busy"
                      checked={availabilityMode === 'busy'}
                      onChange={(e) => setAvailabilityMode(e.target.value)}
                      className="w-4 h-4 text-amber-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">Busy / Taking a Break</p>
                      <p className="text-xs text-slate-500">Hides from searches temporarily</p>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Bio
                  <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Recommended</span>
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={(e) => {
                    handleChange(e);
                    // Clear error when user starts typing
                    if (bioError) setBioError('');
                  }}
                  rows={4}
                  placeholder="What sets you apart? Mention years of experience, specialisations, notable projects..."
                  className={`w-full ${bioError ? 'border-red-300 bg-red-50' : ''}`}
                  data-testid="bio-textarea"
                />
                {bioError ? (
                  <p className="text-xs text-red-600 mt-1 flex items-start gap-1">
                    <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    {bioError}
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">A strong bio helps employers pick you over others. Do not include contact details.</p>
                )}
              </div>

              <div className="flex flex-col gap-3">
                {/* Required: Availability status */}
                {getMissingRequirements().length > 0 && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm" data-testid="publish-requirements">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">To publish your profile:</p>
                      <ul className="text-red-700 mt-1 space-y-0.5">
                        {getMissingRequirements().map((req, i) => (
                          <li key={i}>• {req}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                
                {/* Extended Worker Type Fields */}
                <ExtendedProfileFields workerType={user?.worker_type} />
                
                {/* Recommendations: Optional but helpful */}
                {getMissingRequirements().length === 0 && getProfileRecommendations().length > 0 && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm" data-testid="profile-recommendations">
                    <svg className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-medium text-amber-800">You're ready to publish! Want to boost your profile?</p>
                      <ul className="text-amber-700 mt-1 space-y-0.5">
                        {getProfileRecommendations().map((rec, i) => (
                          <li key={i}>• {rec}</li>
                        ))}
                      </ul>
                      <p className="text-xs text-amber-600 mt-2 italic">These are optional – you can publish now and add them later.</p>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <button 
                    type="submit" 
                    className="btn-secondary"
                    disabled={savingDraft}
                    data-testid="save-draft-btn"
                  >
                    {savingDraft ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button 
                    type="button"
                    onClick={handlePublish}
                    className="btn-accent"
                    disabled={!isProfileComplete() || publishing}
                    title={!isProfileComplete() ? 'Complete all required fields to publish' : 'Publish your profile'}
                    data-testid="publish-profile-btn"
                  >
                    {publishing ? 'Publishing...' : (profileStatus === 'published' ? 'Update & Publish' : 'Publish Profile')}
                  </button>
                  {profile && (
                    <button
                      type="button"
                      onClick={() => setShowProfileForm(false)}
                      className="btn-secondary"
                      data-testid="cancel-btn"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                
                {profileStatus === 'published' && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Your profile is published and visible to employers
                  </p>
                )}
                {profileStatus === 'draft' && (
                  <p className="text-sm text-slate-500">
                    Your profile is saved as a draft and not visible to employers yet
                  </p>
                )}
              </div>
            </form>
          </div>
        )}

        {/* On Call Pro Upgrade Modal */}
        <OnCallProUpgrade
          open={showProUpgrade}
          onOpenChange={setShowProUpgrade}
          onSubscribed={() => {
            fetchProfile(user?.user_id);
            setShowProUpgrade(false);
          }}
        />

        {/* Urgent Job Paywall Modal */}
        <UrgentJobPaywall
          open={showUrgentJobPaywall}
          onOpenChange={setShowUrgentJobPaywall}
          onUpgrade={() => {
            setShowUrgentJobPaywall(false);
            setShowProUpgrade(true);
          }}
          jobDetails={selectedUrgentJob}
        />
      </div>
    </div>
  );
}
